import ServiceModule from '@modules/ServiceModule';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { PcsConversationType } from '@modules/SoftPhoneModule/lib/getPcsConversation';
import { WebphonePhoneStateEvent } from '@modules/SoftPhoneModule/types';
import TasksModule from '../../TasksModule';
import IAppLogger from '@app/lib/IAppLogger';
import InboxModule from '@modules/InboxModule';
import Task from '@modules/TasksModule/Task';
import { TaskState } from '@modules/TasksModule/types';
import { inboxActions, inboxState } from '../state';
import {
  fetchCampaignForTheCall,
  fetchWorkflowNameForTheCall,
} from '../services';
import ToastModule from '@modules/ToastModule';
import { isValidPhoneNumber } from '@pages/settings/phoneNumberUtils';
import FrontEndSettingsModule from '@modules/FrontEndSettingsModule';

/**
 * Resolves queue info from PCS and sets it on the task.
 * Only called when RTCS is down (no session), so queueId/queueName are not yet set.
 */
const resolveQueueFromPcs = (
  task: Task,
  queueIds: string[],
  logger: IAppLogger,
): void => {
  if (queueIds.length === 0) return;
  const latestQueueId = queueIds[queueIds.length - 1];
  const queue = ServiceModule.getQueue(latestQueueId);
  if (!queue) {
    logger.error(`Queue not found for queueId: ${latestQueueId}`);
    return;
  }
  task.queueId = queue.id;
  task.queueName = queue.name;
};

/**
 * Re-keys a task created via the callId to its pcsConversationSid; otherwise
 * the task would be deleted.
 */
const rekeyTaskToConversationSid = (task: Task, pcsConversationSid: string) => {
  // remove the incoming call toast for the callId, a new callToast will be created for the pcsConversationSid
  const tasks = TasksModule.getTasks();
  const hasMultipleTasks =
    tasks.filter((t) => t.attributes.callId === task.attributes.callId).length >
    1;
  if (hasMultipleTasks) {
    ToastModule.hideIncomingCallToast(task.attributes.callId);
  }

  TasksModule.updateTaskId(task, pcsConversationSid);
};

/**
 *  This function is called when a PcsConversation is fetched for a callId.
 *  It updates the task with session related information and the PcsConversation data.
 */
const handlePcsConversationFetched = (
  pcsConversation: PcsConversationType,
  phoneStateUpdateEvent: WebphonePhoneStateEvent,
  logger: IAppLogger,
) => {
  if (!pcsConversation) {
    logger.error(
      `PcsConversation not found for remoteCallId ${phoneStateUpdateEvent.callState.remoteCallId}}`,
    );
    return;
  }
  const { callId } = phoneStateUpdateEvent.callState;

  // get the task for the callId, a task was created with either pcsConversationSid or callId when a session offered event received
  // or a softphone call.created event received
  const task =
    TasksModule.getTask(callId) ?? TasksModule.getTask(pcsConversation.sid);

  if (!task) {
    logger.warn(`task not found for callId: ${callId}}`);
    return;
  }

  const { sid: pcsConversationSid, traits } = pcsConversation;
  const session = ServiceModule.getSessionByConversationSid(pcsConversationSid);

  // Avoid a race that overwrites a Live Services mapped tenant with the PCS org tenant.
  let taskTenantId = session?.tenantId || pcsConversation.tenantId;
  if (!session && ServiceModule.isLiveServicesNetwork() && task.to) {
    taskTenantId = ServiceModule.resolveLiveServicesTenantId(
      Number(pcsConversation.tenantId),
      task.to,
    );
  }

  // if there is a session, update task with session attributes
  if (session) {
    InboxModule.setSessionProperties(task.id, session, session.target.address);
  }

  // update task with information from the pcsConversation
  TasksModule.setTaskAttribute(task.id, 'sessionSid', pcsConversationSid);
  TasksModule.setTaskAttribute(task.id, 'traits', traits);

  // When RTCS is down (no session), resolve queue info from PCS before type
  // evaluation so that evaluateAndSetTaskType can use task.queueId as a fallback
  // signal. When a session exists, queueId is already authoritative from RTCS via
  // setSessionProperties above, so we skip the PCS lookup to avoid overwriting it.
  if (!session) {
    resolveQueueFromPcs(task, pcsConversation.queueIds, logger);
  }

  InboxModule.evaluateAndSetTaskType(task.id);

  // Under retention, re-key in place rather than select: an ended (WrapUp)
  // card must not pull selection when late call data arrives, and a selected
  // card renamed here (an incoming call, selected on offer under its callId)
  // must not record its old id as the previous selection, or a later Close on
  // it would lose the card the CSR was on (REQ-CTL-BOOK-002 AC-9). Selecting
  // it under an unchanged id is already a no-op (state.ts applySelection).
  const oldTaskId = task.id;
  const keepSelectionInPlace =
    InboxModule.isBookingRetentionEnabled() &&
    (task.state === TaskState.WrapUp ||
      inboxState.selectedTaskId === oldTaskId);

  if (task.id !== pcsConversationSid) {
    rekeyTaskToConversationSid(task, pcsConversationSid);
  }

  // we want to start sending interaction data for all calls
  // we should only do this when we have the conversationSid to prevent duplicate data
  // as outbounds are created with callId and then updated with the conversationSid
  TasksModule.setTaskAttribute(pcsConversationSid, 'recordInteraction', true);

  // set tenantId for all calls
  InboxModule.setTenantIdForTask(task, taskTenantId);

  if (keepSelectionInPlace) {
    inboxActions.replaceTaskId(oldTaskId, pcsConversationSid);
  } else {
    InboxModule.setSelectedTaskId(pcsConversationSid);
  }

  // remove the incoming call toast for the callId, a new callToast will be created for the pcsConversationSid
  // ToastModule.hideIncomingCallToast(task.attributes.callId);

  // Fetch campaign for queue calls
  const isValidTenant = +taskTenantId > 0; // For Internal call tenantId = "0"
  const isValidTargetPhoneNumber =
    session?.target?.address && isValidPhoneNumber(session?.target?.address);
  if (
    FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureDisplayCampaignNameOnTaskAndToast,
    ) &&
    isValidTenant &&
    isValidTargetPhoneNumber
  ) {
    fetchCampaignForTheCall({
      tenantId: taskTenantId,
      toPhoneNumber: session.target.address,
      taskId: task.id,
    });
  }

  // Fetch workflow name for calls
  if (
    FrontEndSettingsModule.isContactCenterSettingsEnabled(
      'showWorkflowNameOnInboundCalls',
    ) &&
    isValidTargetPhoneNumber
  ) {
    fetchWorkflowNameForTheCall({
      toPhoneNumber: session.target.address,
      taskId: task.id,
    });
  }
};

export default handlePcsConversationFetched;
